/* iter-72g — Trial Banner.
 *
 * Sits above the AppShell (below the DemoBanner) whenever the signed-
 * in user is on a 14-day trial. Silently displays remaining days and
 * a one-click Upgrade CTA that raises the shared TrialUpgradeDialog
 * without waiting for the once-per-session gating logic.
 *
 * Communication with `<TrialUpgradeDialog />` is via a lightweight
 * DOM CustomEvent (`pw:trial-upgrade-open`) so the two components
 * stay decoupled and don't need a shared React context.
 *
 * Dismissal is per-session — the same UX pattern as DemoBanner.
 */
import React from 'react';
import { Sparkles, ArrowRight, X, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth_context';

function daysLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return 0;
  return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
}

export default function TrialBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = React.useState(false);
  if (!user?.is_trial || dismissed) return null;
  // iter-72h — when the trial has already expired, the takeover screen
  // provides the upgrade CTA. Hide the banner to avoid double signal.
  if (user.trial_expired) return null;

  const remaining = daysLeft(user.trial_expires_at);
  const urgent = remaining !== null && remaining <= 3;

  const openUpgrade = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('pw:trial-upgrade-open'));
  };

  return (
    <div
      data-testid="trial-banner"
      data-urgent={urgent ? 'true' : 'false'}
      className={
        'flex h-9 shrink-0 items-center justify-between gap-3 border-b px-3 text-[12px] '
        + (urgent
          ? 'border-amber-500/40 bg-gradient-to-r from-amber-500/25 via-amber-500/10 to-amber-500/25'
          : 'border-primary/30 bg-gradient-to-r from-primary/15 via-primary/5 to-primary/15')
      }
    >
      <div className="flex min-w-0 items-center gap-2">
        <Clock className={`h-3.5 w-3.5 shrink-0 ${urgent ? 'text-amber-600' : 'text-primary'}`} />
        <span className="truncate">
          <b>Trial</b>
          {remaining !== null && (
            <span data-testid="trial-banner-days" className="text-muted-foreground">
              {' · '}
              {remaining === 0
                ? 'expires today'
                : remaining === 1
                  ? '1 day left'
                  : `${remaining} days left`}
            </span>
          )}
          <span className="hidden text-muted-foreground md:inline">
            {urgent
              ? ' · lock in The Loom before your trial ends'
              : ' · upgrade anytime to keep everything after your trial'}
          </span>
        </span>
      </div>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          className="h-7 gap-1 px-3 text-[11.5px]"
          onClick={openUpgrade}
          data-testid="trial-banner-upgrade"
        >
          {urgent ? 'Upgrade now' : 'See Pro plans'}
          <ArrowRight className="h-3 w-3" />
        </Button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          title="Hide for this session"
          className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
          data-testid="trial-banner-dismiss"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
